import type { Message } from "@ag-ui/core";
import { apiClient, ApiError, apiMutate, isApiError } from "~/utils/apiClient";
import type { PartialAgent } from "./agents";

const { VITE_ANALYSIS_AGENT_API, VITE_CONVERSATIONS_API } = import.meta.env;

export const ANALYSIS_AGENT_API_PREFIX = VITE_ANALYSIS_AGENT_API;

export const conversationPath = (id: string) => `${VITE_ANALYSIS_AGENT_API}/conversations/${id}`;

export const conversationConnectPath = (id: string) =>
  `${VITE_ANALYSIS_AGENT_API}/conversations/${id}/connect`;

export const conversationAguiUrl = (id: string) =>
  `${VITE_ANALYSIS_AGENT_API}/conversations/${id}/messages_agui`;

export const conversationCancelPath = (id: string) =>
  `${VITE_ANALYSIS_AGENT_API}/conversations/${id}/cancel`;

// Share endpoints hit the conversations service directly; they don't need
// the analysis-agent layer (no agent state, no app_id resolution).
export const conversationSharePath = (id: string) => `${VITE_CONVERSATIONS_API}/${id}/share`;

export type ConversationStatus = "READY" | "MESSAGE_IN_PROGRESS";

export interface ConversationConnect {
  state: Record<string, unknown>;
  status: ConversationStatus;
  messages: Message[];
  agent: PartialAgent | null;
}

export interface UpdateConversationInput {
  display_name?: string;
}

export interface ConversationSummary {
  id: string;
  display_name: string;
}

export async function updateConversation(
  id: string,
  input: UpdateConversationInput
): Promise<ConversationSummary> {
  return apiMutate<ConversationSummary>(conversationPath(id), "PATCH", input);
}

export async function deleteConversation(id: string): Promise<void> {
  await apiMutate<null>(conversationPath(id), "DELETE", undefined, { expects204: true });
}

// Signal cancellation of an in-flight run. The orchestrator picks up the
// Redis signal and unwinds; the SSE stream completes naturally and the
// agent emits RUN_FINISHED, so callers don't need to abort locally.
export async function cancelConversation(id: string): Promise<void> {
  await apiMutate<null>(conversationCancelPath(id), "POST", undefined, { expects204: true });
}

export interface ConversationShare {
  conversation_id: string;
  token: string;
  created_by: string;
  created_at: string;
}

// Returns null when no share exists for the conversation. Any other error
// (including network failures) still throws, so the caller can distinguish
// "no link yet" from "the request failed."
export async function getShareLink(conversationId: string): Promise<ConversationShare | null> {
  try {
    return await apiClient<ConversationShare>(conversationSharePath(conversationId));
  } catch (err) {
    if (isApiError(err) && (err as ApiError).status === 404) return null;
    throw err;
  }
}

export async function createShareLink(conversationId: string): Promise<ConversationShare> {
  return apiMutate<ConversationShare>(conversationSharePath(conversationId), "POST");
}

export async function deleteShareLink(conversationId: string): Promise<void> {
  await apiMutate<null>(conversationSharePath(conversationId), "DELETE", undefined, {
    expects204: true,
  });
}
